import { useState, useEffect } from 'react';
import { Bars3Icon, UsersIcon, BuildingOffice2Icon, ClipboardDocumentListIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import { decryptPayload } from '../../../constants/helpers';
import api from '../../../utils/auth/api';

const Dashboard = ({ setIsMobileOpen }) => {

  const defaultFigures = {
    totalClient: 0,
    totalEmployee: 0,
    agreements: { total: 0, active: 0, pending: 0, expired: 0 },
    entries: { total: 0, new: 0, replace: 0, approved: 0, pending: 0 },
  };

  const [loaders, setLoaders] = useState({ fetchStats: false });
  const [figures, setFigures] = useState(defaultFigures);

  // Fetch All (Figures/Stats)
  useEffect(() => {
    const fetchData = async () => {
      setLoaders(prev => ({ ...prev, fetchStats: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/dashboard/admin-stats`);
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setFigures(decryptedData);
        } else {
          setFigures(defaultFigures);
          toast.error(response.data.message);
        }
      } catch (err) {
        setFigures(defaultFigures);
        console.error('Error fetching Admin Dashboard Data:', err);
        toast.error(err?.response?.data.message || "Network Error getting Admin Dashboard Data!")
      } finally {
        setLoaders(prev => ({ ...prev, fetchStats: false }));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Skeleton Loader
  const SkeletonCard = ({ height = 20, width = "full", rounded = "2xl" }) => (
    <div className={`animate-pulse bg-gray-100 rounded-${rounded} w-${width} h-${height}`} ></div>
  );

  return (
    <div className="w-full pb-10">
      {/* Header */}
      <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            className="md:hidden p-2 bg-white shadow rounded-lg"
            onClick={() => setIsMobileOpen(true)}
          >
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide">
            <span className="text-blue-600">Dash</span>
            <span className="text-gray-800">board</span>
          </h1>
        </div>
      </div>

      <hr className="border-t border-gray-200 my-4" />

      {/* Stats */}
      {loaders.fetchStats ? (
        <div className="space-y-8 mt-10">
          {/* TOP METRIC CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((_, idx) => (
              <div
                key={idx}
                className="p-6 rounded-3xl bg-gray-300 shadow-lg"
              >
                <SkeletonCard height={6} width="3/4" rounded="xl" />
                <div className="mt-4">
                  <SkeletonCard height={10} width="full" rounded="xl" />
                </div>
              </div>
            ))}
          </div>

          {/* AGREEMENT STATUS & ENTRY BREAKDOWN */}
          <div className="grid grid-cols-1 gap-6">
            {[1, 2].map((_, i) => (
              <div key={i} className="p-6 rounded-3xl bg-gray-300 shadow-md border border-gray-200">
                <SkeletonCard height={6} width="1/3" rounded="xl" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  {[1, 2, 3, 4].map((_, j) => (
                    <SkeletonCard key={j} height={16} width="full" rounded="2xl" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* TOP METRIC CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                label: "Total Clients",
                value: figures?.totalClient,
                icon: BuildingOffice2Icon,
                color: "from-indigo-500 to-indigo-600",
              },
              {
                label: "Total Employees",
                value: figures?.totalEmployee,
                icon: UsersIcon,
                color: "from-emerald-500 to-emerald-600",
              },
              {
                label: "Total Entries",
                value: figures?.entries.total,
                icon: ClipboardDocumentListIcon,
                color: "from-amber-500 to-amber-600",
              },
              {
                label: "Total Agreements",
                value: figures?.agreements.total,
                icon: DocumentTextIcon,
                color: "from-blue-500 to-blue-600",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-3xl bg-gradient-to-br ${item.color} text-white shadow-lg hover:shadow-2xl transition duration-300`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm opacity-80">{item.label}</p>
                    <h2 className="text-3xl font-bold mt-1">{item.value || 0}</h2>
                  </div>
                  <item.icon className="h-10 w-10 opacity-70" />
                </div>
              </div>
            ))}
          </div>

          {/* AGREEMENT + ENTRY SECTION */}
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white rounded-3xl shadow-md p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Entry Breakdown
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: "New", value: figures?.entries.new, color: "text-blue-600" },
                  { label: "Replace", value: figures?.entries.replace, color: "text-blue-600" },
                  { label: "Pending", value: figures?.entries.pending, color: "text-amber-600" },
                  { label: "Approved", value: figures?.entries.approved, color: "text-green-600" },
                ].map((e, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-4 text-center">
                    <p className="text-xs text-gray-500">{e.label}</p>
                    <p className={`text-2xl font-bold ${e.color}`}>{e.value || 0}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-md p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Agreement Status
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: "Pending", value: figures?.agreements.pending, color: "text-amber-600" },
                  { label: "Active", value: figures?.agreements.active, color: "text-green-600" },
                  { label: "Expired", value: figures?.agreements.expired, color: "text-red-500" },
                ].map((a, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-4 text-center">
                    <p className="text-xs text-gray-500">{a.label}</p>
                    <p className={`text-2xl font-bold ${a.color}`}>{a.value || 0}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;